import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { KeyboardEvent } from 'react';

export type ChartMenuAction = {
  label: string; run: () => void; disabled?: boolean; checked?: boolean; danger?: boolean; divider?: boolean;
};

export function ChartContextMenu({ x, y, title, subtitle, actions, onClose }: {
  x: number; y: number; title: string; subtitle: string; actions: ChartMenuAction[];
  onClose: (restoreFocus?: boolean) => void;
}) {
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: x, top: y });
  useLayoutEffect(() => {
    const node = menu.current!;
    const bounds = node.getBoundingClientRect();
    setPosition({
      left: Math.max(8, Math.min(x, window.innerWidth - bounds.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - bounds.height - 8)),
    });
    node.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus({ preventScroll: true });
    const outside = (event: globalThis.PointerEvent) => {
      if ((event.target as Element).closest?.('[data-chart-menu-trigger]')) return;
      if (!node.contains(event.target as Node)) onClose(false);
    };
    const dismiss = () => onClose(false);
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('resize', dismiss);
    const scroll = (event: Event) => { if (!(event.target instanceof Node) || !node.contains(event.target)) dismiss(); };
    window.addEventListener('scroll', scroll, true);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', scroll, true);
    };
  }, [x, y, onClose]);

  function keyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault(); event.stopPropagation(); onClose(true); return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = [...menu.current!.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
      : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus({ preventScroll: true });
  }

  return createPortal(<div ref={menu} className="zp-context-menu" role="menu" aria-label="ZonePips chart menu"
    data-testid="chart-context-menu" style={position} onKeyDown={keyDown} onContextMenu={event => event.preventDefault()}>
    <div className="zp-context-heading" role="presentation"><strong>{title}</strong><span>{subtitle}</span></div>
    {actions.map(action => <button key={action.label} type="button"
      role={action.checked === undefined ? 'menuitem' : 'menuitemcheckbox'} aria-checked={action.checked}
      disabled={action.disabled} className={(action.danger ? 'zp-context-danger ' : '') + (action.divider ? 'zp-context-divider' : '')}
      onClick={() => { onClose(true); action.run(); }}>
      <span>{action.label}</span>{action.checked !== undefined && <b aria-hidden="true">{action.checked ? 'ON' : 'OFF'}</b>}
    </button>)}
    <button type="button" role="menuitem" className="zp-context-divider" onClick={() => onClose(true)}>
      <span>Tutup menu</span><small>Esc</small>
    </button>
  </div>, document.body);
}
